import {
  getTheme,
  mergeStyleSets,
  PrimaryButton,
  DefaultButton,
  Stack,
  Text,
  Icon,
  Image,
} from '@fluentui/react';
import * as React from 'react';
import { navigateTo } from '../../../src/hooks';
import { MsCertifiedBadge } from '../../MsCertifiedBadge';
const theme = getTheme();

// Product logos
const WOO_LOGO = 'https://reenhanced.com/wp-content/uploads/2022/03/logo.jpg';
const GF_PRO_LOGO = 'https://reenhanced.com/wp-content/uploads/2024/03/gf-pro-with-background.jpg';
const PF7_LOGO = 'https://reenhanced.com/wp-content/uploads/2020/10/icon.png';
const WPFORMS_LOGO = 'https://reenhanced.com/wp-content/uploads/2023/09/combined-logo-1.png';
const WPC_LOGO = 'https://reenhanced.com/wp-content/uploads/2025/04/WPPowerPro-LOGO.png';

const style = mergeStyleSets({
  container: {},

  heroBanner: {
    background: `linear-gradient(135deg, ${theme.palette.themePrimary} 0%, ${theme.palette.themeDarker} 100%)`,
    color: '#fff',
    borderRadius: 12,
    padding: '40px 40px 36px',
    marginTop: 16,
    marginBottom: 32,
    textAlign: 'center' as const,
  },
  heroTitle: [
    theme.fonts.superLarge,
    { color: '#fff', margin: 0, marginBottom: 8, fontSize: 36 },
  ],
  heroSubtitle: [
    theme.fonts.large,
    { color: 'rgba(255,255,255,0.85)', marginBottom: 20, display: 'block' },
  ],
  heroTagline: [
    theme.fonts.medium,
    { color: 'rgba(255,255,255,0.7)', marginBottom: 0, display: 'block' },
  ],
  certRow: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 10,
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 20,
    marginBottom: 32,
  },

  card: {
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 10,
    padding: '20px 20px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
    transition: 'box-shadow 0.2s',
    cursor: 'pointer',
    ':hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
    },
  },
  featuredCard: {
    border: `2px solid #f0a500`,
    background: 'linear-gradient(135deg, #fffbf0 0%, #fff 100%)',
  },
  featuredPill: {
    alignSelf: 'flex-start' as const,
    backgroundColor: '#f0a500',
    color: '#fff',
    padding: '2px 10px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 10,
    letterSpacing: 0.5,
  },
  cardLogo: {
    height: 40,
    width: 40,
    objectFit: 'cover' as const,
    borderRadius: 8,
    marginBottom: 12,
  },
  cardLogoWide: {
    height: 40,
    width: 'auto' as const,
    maxWidth: 120,
    objectFit: 'contain' as const,
    marginBottom: 12,
  },
  cardTitle: [theme.fonts.large, { fontWeight: 700, marginBottom: 6, color: theme.palette.neutralPrimary }],
  cardDesc: [theme.fonts.medium, { color: theme.palette.neutralSecondary, flexGrow: 1, marginBottom: 12 }],
  cardPrice: [theme.fonts.small, { color: theme.palette.themePrimary, fontWeight: 600, marginBottom: 10 }],
  cardBadge: {
    marginTop: 'auto' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    gap: 8,
  },

  sectionTitle: [theme.fonts.xLarge, { fontWeight: 700, marginBottom: 12, marginTop: 8 }],

  valueRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  valueBox: {
    backgroundColor: theme.palette.neutralLighterAlt,
    border: `1px solid ${theme.palette.neutralLight}`,
    borderRadius: 8,
    padding: '16px 20px',
    textAlign: 'center' as const,
  },
  valueIcon: {
    fontSize: 28,
    color: theme.palette.themePrimary,
    marginBottom: 8,
    display: 'block',
  },
  valueTitle: [theme.fonts.mediumPlus, { fontWeight: 700, marginBottom: 4 }],
  valueText: [theme.fonts.small, { color: theme.palette.neutralSecondary }],
});

interface ProductCard {
  key: string;
  title: string;
  description: string;
  price: string;
  logo: string;
  logoWide?: boolean;
  path: string;
  featured?: boolean;
  certified: boolean;
  externalUrl: string;
}

const products: ProductCard[] = [
  {
    key: 'wpconnectrpro',
    title: 'WP Connectr Pro',
    description: 'Transform WordPress into an Enterprise Data Platform. Import Power Platform solutions, bulk CSV import (10,000+ records), and bidirectional real-time sync — without a single line of code.',
    price: 'From $1,500 / year – custom enterprise pricing available',
    logo: WPC_LOGO,
    path: '/products/wp-connectr-pro',
    featured: true,
    certified: true,
    externalUrl: 'https://reenhanced.com/product/wp-connectr-pro/',
  },
  {
    key: 'gfpro',
    title: 'Gravity Forms Pro',
    description: 'The professional Power Automate connector for Gravity Forms. Full read/write access, unlimited forms and users, customer portal support, and enterprise security — direct to Power Automate.',
    price: 'From $39 / month — 60-day free trial',
    logo: GF_PRO_LOGO,
    path: '/products/gravity-forms-pro',
    certified: true,
    externalUrl: 'https://reenhanced.com/product/gravity-forms-power-automate-pro/',
  },
  {
    key: 'woopower',
    title: 'Power Automate for WooCommerce',
    description: 'Integrate your WooCommerce store with over 1,000 services via Microsoft Power Automate. Supports orders, subscriptions, products, coupons, and customers with triggers and actions.',
    price: '$299 / year',
    logo: WOO_LOGO,
    path: '/products/woopower',
    certified: true,
    externalUrl: 'https://reenhanced.com/products/power-automate-for-woocommerce/',
  },
  {
    key: 'pf7',
    title: 'Power Form 7',
    description: 'Connect Contact Form 7 to Power Automate and 1,000+ services. Trigger flows on submissions or submit forms from flows. The most useful CF7 plugin ever made.',
    price: 'From $19 / month — free trial available',
    logo: PF7_LOGO,
    path: '/products/power-form-7',
    certified: true,
    externalUrl: 'https://reenhanced.com/products/power-form-7/',
  },
  {
    key: 'wpforms',
    title: 'Power Automate for WPForms',
    description: 'The Microsoft Certified connector for WPForms. Trigger flows from any form submission or query past entries from Power Automate. Data goes directly to Microsoft — never through our servers.',
    price: 'From $29 / month — free trial available',
    logo: WPFORMS_LOGO,
    logoWide: true,
    path: '/products/wpforms',
    certified: true,
    externalUrl: 'https://reenhanced.com/product/power-automate-for-wpforms/',
  },
];

const valuePillars = [
  { icon: 'Certificate', title: 'Microsoft Certified', text: 'All connectors are officially certified in the Microsoft Power Automate gallery.' },
  { icon: 'Code', title: 'Zero Code Required', text: 'Point-and-click automation in Power Automate — no development skills needed.' },
  { icon: 'Lock', title: 'Privacy First', text: 'Your data goes directly to Power Automate. We never store your form submissions.' },
  { icon: 'Rocket', title: 'Fast Time-to-Value', text: 'Deploy in hours or days — not months. Proven WordPress ecosystem, no surprises.' },
];

export const ProductsOverview: React.FC = () => {
  return (
    <div className={style.container}>
      {/* Hero */}
      <div className={style.heroBanner}>
        <h1 className={style.heroTitle}>
          WordPress + Power Automate, Perfected
        </h1>
        <Text className={style.heroSubtitle}>
          Reenhanced builds the deepest, most reliable WordPress connectors for Microsoft Power Automate —
          so your website works as hard as the rest of your Microsoft ecosystem.
        </Text>
        <Text className={style.heroTagline}>
          Connect forms, stores, and enterprise data platforms to 1,000+ services. Zero code required.
        </Text>
        <div className={style.certRow}>
          <MsCertifiedBadge style={{ filter: 'brightness(2)' }} />
          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>All products are Microsoft Certified</Text>
        </div>
      </div>

      {/* Value pillars */}
      <div className={style.valueRow}>
        {valuePillars.map((p) => (
          <div key={p.icon} className={style.valueBox}>
            <Icon iconName={p.icon} className={style.valueIcon} />
            <div className={style.valueTitle}>{p.title}</div>
            <div className={style.valueText}>{p.text}</div>
          </div>
        ))}
      </div>

      {/* Products grid */}
      <div className={style.sectionTitle}>Our Products</div>
      <div className={style.grid}>
        {products.map((product) => (
          <div
            key={product.key}
            className={`${style.card} ${product.featured ? style.featuredCard : ''}`}
            onClick={() => navigateTo(product.path)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigateTo(product.path)}
          >
            {product.featured && <div className={style.featuredPill}>&#9733; Featured Product</div>}

            <img
              src={product.logo}
              alt={product.title}
              className={product.logoWide ? style.cardLogoWide : style.cardLogo}
            />

            <div className={style.cardTitle}>{product.title}</div>
            <div className={style.cardDesc}>{product.description}</div>
            <div className={style.cardPrice}>{product.price}</div>

            <div className={style.cardBadge}>
              {product.certified && <MsCertifiedBadge style={{ width: 80 }} />}
              <DefaultButton
                text="Learn More"
                iconProps={{ iconName: 'ChevronRight' }}
                onClick={(e) => { e.stopPropagation(); navigateTo(product.path); }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* CTA footer */}
      <div style={{
        marginTop: 24,
        backgroundColor: theme.palette.neutralLighterAlt,
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 10,
        padding: '24px 32px',
        textAlign: 'center',
        marginBottom: 24,
      }}>
        <Text variant="xLarge" block style={{ fontWeight: 700, marginBottom: 8 }}>
          Not sure which product is right for you?
        </Text>
        <Text block style={{ color: theme.palette.neutralSecondary, marginBottom: 16 }}>
          Each plugin works with the same Microsoft Power Automate infrastructure. Pick the one that matches your
          WordPress setup, or mix and match across your sites.
        </Text>
        <PrimaryButton
          text="Contact Reenhanced"
          iconProps={{ iconName: 'Mail' }}
          onClick={() => { window.location.href = 'https://reenhanced.com/contact/'; }}
        />
      </div>
    </div>
  );
};

export default ProductsOverview;
